import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2, XCircle, Clock, RefreshCw, Sparkles, ChevronRight, ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { API } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

export default function PaymentResult() {
  const { t } = useTranslation();
  const fmtXof = useFmtXof();
  const [params] = useSearchParams();
  const paymentId = params.get("paymentId");
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!paymentId) {
      setError(t("paymentResult.missingId"));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payments/${paymentId}`);
      setPayment(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [paymentId, t]);

  useEffect(() => {
    load();
    // auto-refresh once after 4s if still pending (FedaPay webhook race)
    const tm = setTimeout(load, 4000);
    return () => clearTimeout(tm);
  }, [load]);

  const status = (payment?.status || "").toUpperCase();
  const isPaid = status === "PAID";
  const isFailed = status === "FAILED";

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 bg-white relative overflow-hidden" data-testid="payment-result-page">
      <span className="geo-shape bg-[#16A34A]/12 h-72 w-72 -top-20 -right-20" aria-hidden />
      <span className="geo-shape bg-[#16A34A]/10 h-64 w-64 -bottom-20 -left-20" aria-hidden />

      <Card className="relative w-full max-w-lg border-[#E5EBE7] p-8 rounded-3xl">
        {loading && !payment ? (
          <div className="text-center py-10">
            <Clock className="h-10 w-10 text-[#16A34A] mx-auto mb-3 animate-spin" />
            <p className="text-[#4B5563]">{t("paymentResult.verifying")}</p>
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h1 className="font-display text-2xl text-[#0A2518]">{t("paymentResult.cantLoad")}</h1>
            <p className="mt-2 text-sm text-[#4B5563]">{error}</p>
            <Button asChild className="mt-6 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
              <Link to="/dashboard">{t("paymentResult.backToDash")}</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center">
              {isPaid ? (
                <CheckCircle2 className="h-14 w-14 text-[#16A34A] mx-auto mb-3" data-testid="payment-status-paid" />
              ) : isFailed ? (
                <XCircle className="h-14 w-14 text-red-500 mx-auto mb-3" data-testid="payment-status-failed" />
              ) : (
                <Clock className="h-14 w-14 text-yellow-500 mx-auto mb-3 animate-pulse" data-testid="payment-status-pending" />
              )}
              <span className="pill"><Sparkles className="h-3.5 w-3.5" /> {t("paymentResult.pillPay")}</span>
              <h1 className="mt-4 font-display text-3xl font-bold text-[#0A2518]">
                {isPaid ? t("paymentResult.successTitle") : isFailed ? t("paymentResult.failedTitle") : t("paymentResult.pendingTitle")}
              </h1>
              <p className="mt-3 text-[#4B5563]">
                {isPaid ? t("paymentResult.successDesc") : isFailed ? t("paymentResult.failedDesc") : t("paymentResult.pendingDesc")}
              </p>
            </div>

            <div className="mt-6 rounded-xl bg-[#F0FDF4] border border-[#16A34A]/20 p-4 text-sm">
              <Row label={t("paymentResult.subject")} value={payment.subject} />
              <Row label={t("paymentResult.duration")} value={payment.hours ? `${payment.hours} h` : "—"} />
              <Row label={t("paymentResult.total")} value={fmtXof(payment.amount_xof)} bold />
              <Row label={t("paymentResult.fee")} value={fmtXof(payment.platform_fee_xof)} muted />
              <Row label={t("paymentResult.educatorShare")} value={fmtXof(payment.educator_net_xof)} muted />
              {payment.fedapay_transaction_id && (
                <Row label={t("paymentResult.ref")} value={String(payment.fedapay_transaction_id)} muted small />
              )}
            </div>

            <div className="mt-2 flex items-center gap-2 text-xs text-[#4B5563]">
              <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" /> {t("paymentResult.secure")}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {!isPaid && !isFailed && (
                <Button onClick={load} variant="outline" className="rounded-full border-[#16A34A] text-[#16A34A]" data-testid="payment-refresh-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> {t("paymentResult.refresh")}
                </Button>
              )}
              {isFailed && payment.payment_url && (
                <Button asChild className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                  <a href={payment.payment_url}>{t("paymentResult.retry")}</a>
                </Button>
              )}
              <Button asChild className={`rounded-full ${isPaid ? "bg-[#16A34A] hover:bg-[#15803D] text-white" : "bg-white border border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"}`} data-testid="payment-go-dashboard">
                <Link to="/dashboard">
                  {isPaid ? t("paymentResult.viewBookings") : t("paymentResult.dashboard")} <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

const Row = ({ label, value, bold, muted, small }) => (
  <div className={`flex items-center justify-between ${small ? "mt-1 text-[11px]" : "mt-1"}`}>
    <span className={muted ? "text-[#4B5563]" : "text-[#0A2518]"}>{label}</span>
    <span className={`${bold ? "font-semibold text-[#0A2518]" : muted ? "text-[#4B5563]" : "text-[#0A2518]"}`}>{value}</span>
  </div>
);
